package com.ives.modules.crosssection;

import haxe.Timer;

import js.html.TouchEvent;
import js.html.MouseEvent;

import com.ives.components.workspace.DicomWorkspace;
import com.ives.components.viewer.DicomViewer;
import com.ives.utils.Config;
import com.ives.modules.Drawing;
import com.ives.modules.ModuleEvent;
import com.ives.utils.Log;
import com.ives.utils.math.Vector2D;
import com.ives.utils.math.Vector3D;

class CrossSection extends Module{
	static private inline var CROSS_SECTION_MODULE:String = "CROSS_SECTION_MODULE";
	static private inline var CROSS_SECTION_SELECT_WORKSPACE:String = "CROSS_SECTION_SELECT_WORKSPACE";
	static private inline var CROSS_SECTION_VIEW_WORKSPACE:String = "CROSS_SECTION_VIEW_WORKSPACE";
	static private inline var CROSS_SECTION_EVENT:String = "CROSS_SECTION_POINTS";

	static private inline var tension:Float = 0.5;
	static private inline var segments:UInt = 25;

	private var points:Array<Float> = [];

	private var lastTouch:Vector3D;
	private var lastTap:Float = 0;

	private var direction:Vector3D = new Vector3D(0, 0, -1);

	override public function init(dicomViewer:DicomViewer, ?dicomWorkspace:DicomWorkspace){
		super.init(dicomViewer, dicomWorkspace);

		if(getWorkspaceMode() != CROSS_SECTION_SELECT_WORKSPACE)
			return;

		dicomWorkspace.on("mousedown", onMouseDown);
		dicomWorkspace.on("mousemove", onMouseMove);
		dicomWorkspace.on("mouseup", onMouseUp);

		dicomWorkspace.on("touchstart", onTouchStart);
		dicomWorkspace.on("touchmove", onTouchMove);
		dicomWorkspace.on("touchend", onTouchEnd);

		dicomWorkspace.on("dblclick", onDoubleClick);

		Log.debug('CrossSection init');
	}

	override public function handleModuleEvent(event:ModuleEvent){
		if(event.type == CROSS_SECTION_MODULE){
			if(getWorkspaceMode() == CROSS_SECTION_SELECT_WORKSPACE || getWorkspaceMode() == CROSS_SECTION_VIEW_WORKSPACE)
				return;

			Log.debug('Initializing...');

			dispatchEvent(new ModuleEvent(DicomViewer.LAYOUT_CHANGE, [2, 1], [CROSS_SECTION_SELECT_WORKSPACE, CROSS_SECTION_VIEW_WORKSPACE]));
		}

		if(event.type == CROSS_SECTION_EVENT){
			if(getWorkspaceMode() == CROSS_SECTION_VIEW_WORKSPACE){
				Log.debug('Reconstructing...');

				var temp:String = StringTools.replace(event.points.toString(), '[', '');
				temp = StringTools.replace(temp, ']', '');

				var pixelSpacing = dicomWorkspace.dicomImageData.pixelSpacing[0];

				dicomWorkspace.dicomImage.setBaseUrl('${Config.SERVER_URL}/dicom/${dicomViewer.originalDicomImageData.sourceName}/context/${dicomViewer.originalDicomImageData.id}/crossSection?direction=${direction}&tension=${tension}&segments=${segments}&spacing=${pixelSpacing}&points=${temp}');
			}
			else if(getWorkspaceMode() == CROSS_SECTION_SELECT_WORKSPACE){
				var curve:Array<Float> = CardinalSpline.getCurvePoints(pointsToFloats2D(volumeToLocal(floatsToPoints3D(event.points))), 0.5, 25, false);

				addDrawing( new Drawing(Drawing.SHAPE, pointsToFloats3D(localToVolume(floatsToPoints2D(curve))), event.points, !event.editable, onModified) );
			}
		}

		if(event.type == DicomWorkspace.WORKSPACE_READY && getWorkspaceMode() == CROSS_SECTION_SELECT_WORKSPACE){
			Log.info("Cross Section init...");

			var min = Vector3D.fromArray(dicomWorkspace.dicomContextData.min);
			var max = Vector3D.fromArray(dicomWorkspace.dicomContextData.max);
			var dimnesions = max.clone().subtract(min);
			var hd = dimnesions.clone().scale(0.5);

			var sphereRadius = Math.max(hd.x, Math.max(hd.y, hd.z)); //Math.sqrt(hd.x * hd.x + hd.y * hd.y + hd.z * hd.z);
			var center = min.clone().add(dimnesions.clone().scale(0.5));

			var columnDirection:Vector3D = new Vector3D(1, 0, 0);
			var rowDirection:Vector3D = new Vector3D(0, 1, 0);
			var planeNormal:Vector3D = new Vector3D(0, 0, -1); // we are looking top down

			var position = center.clone()
			.subtract(rowDirection.clone().scale(sphereRadius)) // move to left edge
			.subtract(columnDirection.clone().scale(sphereRadius)); // move to top edge
			//.add(planeNormal.clone().scale(center.clone().subtract(center).dot(planeNormal))); // move to correct depth
			var screen:Vector2D = new Vector2D(2*sphereRadius, 2*sphereRadius);
			var spacing = dicomWorkspace.dicomImageData.pixelSpacing[0];

			dicomWorkspace.dicomImage.setBaseUrl('${Config.SERVER_URL}/dicom/${dicomViewer.originalDicomImageData.sourceName}/context/${dicomViewer.originalDicomImageData.id}/slice?position=${position}&columnDirection=${columnDirection}&rowDirection=${rowDirection}&screen=${screen}&spacing=${spacing}');
		}

		if(event.type == DicomViewer.RESIZED)
			redraw();
	}

	private function onModified(drawing:Drawing){
		dispatchEvent(new ModuleEvent(CROSS_SECTION_EVENT, drawing.handlePoints, true, true));
	}

	private function onTouchStart(event:TouchEvent){
		if(getWorkspaceMode() != CROSS_SECTION_SELECT_WORKSPACE)
			return;

		event.preventDefault();

		lastTouch = touchToVolume(event);

		onDown(lastTouch);
	}

	private function onMouseDown(event:MouseEvent){
		if(hasModifier(event))
			return;

		onDown(mouseToVolume(event));
	}

	private function onDown(image:Vector3D){
		if(!dicomViewer.reconstructionsButtonChecked())
			return;

		if(getWorkspaceMode() != CROSS_SECTION_SELECT_WORKSPACE)
			return;

		onDrawingMouseDown(image.x, image.y, image.z);
	}

	private function onTouchMove(event:TouchEvent){
		lastTouch = touchToVolume(event);

		onMove(lastTouch);
	}

	private function onMouseMove(event:MouseEvent){
		if(hasModifier(event))
			return;

		onMove(mouseToVolume(event));
	}

	private function onMove(image:Vector3D){
		if(!dicomViewer.reconstructionsButtonChecked())
			return;

		if(getWorkspaceMode() != CROSS_SECTION_SELECT_WORKSPACE)
			return;

		if(onDrawingMouseMove(image.x, image.y, image.z))
			return;

		if(points.length == 0)
			return;

		var temp:Array<Float> = points.copy();

		temp.push(image.x);
		temp.push(image.y);
		temp.push(image.z);

		dispatchEvent(new ModuleEvent(CROSS_SECTION_EVENT, temp, false, true));
	}

	private function onTouchEnd(event:TouchEvent){
		var now:Float = Timer.stamp();

		if(now - lastTap < 0.5){
			onDouble(true);

			lastTap = now;

			return;
		}

		lastTap = now;

		onUp(lastTouch);
	}

	private function onMouseUp(event:MouseEvent){
		if(hasModifier(event))
			return;

		onUp(mouseToVolume(event));
	}

	private function onUp(image:Vector3D){
		if(!dicomViewer.reconstructionsButtonChecked())
			return;

		if(getWorkspaceMode() != CROSS_SECTION_SELECT_WORKSPACE)
			return;

		if(onDrawingMouseUp(image.x, image.y, image.z))
			return;

		points.push(image.x);
		points.push(image.y);
		points.push(image.z);

		if(points.length > 3)
			dispatchEvent(new ModuleEvent(CROSS_SECTION_EVENT, points, false, true));
	}

	private function onDoubleClick(){
		onDouble(false);
	}

	private function onDouble(touch:Bool){
		if(!dicomViewer.reconstructionsButtonChecked())
			return;

		if(getWorkspaceMode() != CROSS_SECTION_SELECT_WORKSPACE)
			return;

		if(points.length > 6){
			if(!touch){
				points.pop();
				points.pop();
				points.pop();
			}

			dispatchEvent(new ModuleEvent(CROSS_SECTION_EVENT, points, true, true));
		}

		points = [];
	}
}
